import { useState, type FormEvent } from "react";
import PageLayout from "@/components/feature/PageLayout";

const FORM_URL = "https://readdy.ai/api/form/db785t6n6nb9f68e9an0";

const SERVICES = [
  { icon: "ri-tv-2-line", title: "影视内容聚合", desc: "汇集电视剧、电影与综艺，覆盖都市、年代、古装、悬疑、爱情等多元题材。" },
  { icon: "ri-search-eye-line", title: "便捷查找与筛选", desc: "支持关键词模糊搜索与多维度筛选排序，帮助观众快速定位想看的内容。" },
  { icon: "ri-play-circle-line", title: "清晰剧集目录", desc: "详情页提供完整剧集目录与更新状态，点击即可进入对应的播放页面。" },
  { icon: "ri-heart-3-line", title: "本地收藏", desc: "收藏喜欢的影视作品，数据保存在本机浏览器，随时快速找回。" },
];

const GUIDE = [
  "在首页或导航栏选择电视剧、电影、综艺频道，浏览对应分类内容。",
  "使用分类页的筛选与排序，按类型、年份、地区快速缩小范围。",
  "点击任意封面进入详情页，查看剧情简介、剧集目录与相关推荐。",
  "在详情页点击集数或“立即播放”进入播放页，演示播放器会展示播放状态。",
  "点击收藏按钮可收藏作品，再次点击取消收藏。",
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      // Silently treat bots as success without sending anything.
      setStatus("success");
      setMessage("感谢您的反馈，我们会尽快查看！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("感谢您的反馈，我们会尽快查看！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg);
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <PageLayout
      title="关于我们 - 桔子TV影视娱乐平台"
      description="了解桔子TV的品牌定位、服务范围与联系方式。桔子TV专注影视内容在线观看与信息展示，为观众提供便捷清晰的追剧体验。"
    >
      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden border-b border-background-200">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20abstract%20banner%20of%20warm%20amber%20light%20streaks%20and%20soft%20film%20grain%20over%20a%20deep%20charcoal%20background%2C%20elegant%20minimal%20entertainment%20brand%20mood%2C%20artistic%20composition%2C%20high%20detail&width=1920&height=720&seq=juzi-about-banner&orientation=landscape"
          alt="桔子TV 品牌横幅"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/20" />
        <div className="relative z-10 h-full max-w-[1280px] mx-auto px-4 md:px-8 flex flex-col justify-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/15 text-primary-400 text-xs font-medium font-label w-fit">
            <i className="ri-leaf-line" />
            ABOUT JUZI TV
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 mt-4">关于桔子TV</h1>
          <p className="text-sm md:text-base text-foreground-700 mt-3 max-w-2xl leading-relaxed">
            一个专注于影视内容在线观看与信息展示的平台，让每一次追剧都清晰、顺手、愉快。
          </p>
        </div>
      </section>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500" />
              品牌介绍
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">
              桔子TV 以电视剧《好好的时光》为特色内容，同时聚合电影、综艺等丰富影视资源，致力于为观众提供一站式的影视浏览体验。我们希望用户无需在繁杂的信息中迷失，而是通过清晰的分类、精准的搜索与贴心的推荐，快速找到想看的作品，享受流畅的追剧时光。
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-loose mt-4">
              平台面向喜欢在线观看电视剧和影视作品的年轻及中年观众，支持 PC 与手机端随时浏览。我们注重内容的层级分明与阅读舒适，让查找、选择与播放都变得简单直接。
            </p>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              联系方式
            </h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-500/15 text-secondary-300 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-foreground-600 text-xs">客服邮箱</p>
                  <p className="text-foreground-950 mt-0.5">support@juzi-tv.example.com</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-500/15 text-secondary-300 flex items-center justify-center shrink-0">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-foreground-600 text-xs">服务时间</p>
                  <p className="text-foreground-950 mt-0.5">每天 09:00 - 22:00</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-500/15 text-secondary-300 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line" />
                </span>
                <div>
                  <p className="text-foreground-600 text-xs">运营地址</p>
                  <p className="text-foreground-950 mt-0.5">中国 · 线上运营</p>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section className="mt-10 md:mt-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-primary-500" />
            服务范围
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((service) => (
              <div key={service.title} className="rounded-lg bg-background-100 border border-background-200 p-5">
                <span className="w-11 h-11 rounded-lg bg-primary-500/15 text-primary-400 flex items-center justify-center mb-3">
                  <i className={`${service.icon} text-2xl`} />
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-950">{service.title}</h3>
                <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mt-10 md:mt-14">
          <section className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500" />
              使用说明
            </h2>
            <ol className="space-y-3">
              {GUIDE.map((step, index) => (
                <li key={step} className="flex gap-3 text-sm text-foreground-700 leading-relaxed">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-primary-500 text-background-50 text-xs font-semibold flex items-center justify-center font-label">
                    {index + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
            <p className="text-xs text-foreground-600 mt-5 p-3 rounded-md bg-background-200/60 leading-relaxed">
              <i className="ri-information-line mr-1" />
              温馨提示：本站影视信息用于内容展示，播放页面为功能演示，不提供真实视频流与支付功能。
            </p>
          </section>

          <section className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-1.5 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              用户反馈
            </h2>
            <p className="text-sm text-foreground-600 mb-5">有任何建议或内容需求，欢迎告诉我们。</p>

            <form data-readdy-form id="db785t6n6nb9f68e9an0" onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="feedback-name" className="block text-xs text-foreground-600 mb-1.5">您的称呼</label>
                  <input
                    id="feedback-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼您"
                    className="w-full h-11 px-3 rounded-md bg-background-200/60 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-600 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/40 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="feedback-email" className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full h-11 px-3 rounded-md bg-background-200/60 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-600 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/40 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="feedback-topic" className="block text-xs text-foreground-600 mb-1.5">反馈类型</label>
                <select
                  id="feedback-topic"
                  name="topic"
                  defaultValue="内容建议"
                  className="w-full h-11 px-3 rounded-md bg-background-200/60 border border-background-300 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="播放问题">播放问题</option>
                  <option value="功能需求">功能需求</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="feedback-message" className="block text-xs text-foreground-600 mb-1.5">反馈内容</label>
                <textarea
                  id="feedback-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述您的建议或遇到的问题（最多 500 字）"
                  className="w-full px-3 py-2.5 rounded-md bg-background-200/60 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-600 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/40 transition-colors resize-none"
                />
              </div>

              <input
                className="form-note-aux"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin-slow" />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    提交反馈
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="text-sm text-secondary-300 flex items-center gap-1.5 animate-fade-in">
                  <i className="ri-checkbox-circle-line" />
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="text-sm text-accent-400 flex items-center gap-1.5 animate-fade-in">
                  <i className="ri-error-warning-line" />
                  {message}
                </p>
              )}
            </form>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}